import React, { useState, createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useBroadcast, usePopout, useSubscribe } from '../common/hooks/broadcastHook';
import { resetNewJob, selectActiveJob, setActiveJob, updateJob } from '../reducers/jobSlice';
import useNetwork from '../common/hooks/networkHook';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveScheduledJob, setActiveScheduledJob } from '../reducers/scheduledJobSlice';
import PropTypes from 'prop-types';

const CADContext = createContext();

export const CADProvider = ({ init, children }) => {
  const broadcast = useBroadcast();
  const network = useNetwork();
  const dispatch = useDispatch();
  const [callsign, setCallsign] = useState(init?.callsign);
  const [pcrID, setPcrID] = useState(init?.pcrID);
  const [listEntryID, setListEntryID] = useState(init?.listEntryID);
  const [listID, setListID] = useState(init?.listID);
  const [locationID, setLocationID] = useState(init?.locationID);
  const [requestableJobId, setRequestableJobId] = useState(init?.requestableJobId);
  const popout = usePopout();

  const jobId = useSelector(selectActiveJob)
  const scheduledJobId = useSelector(selectActiveScheduledJob);

  useEffect(() => { resetNewJob(network) }, [network]);

  useSubscribe('selectJob', ({ jobid }) => doSetJobId(jobid || undefined, true));
  useSubscribe('selectRequestableJob', ({ requestablejobid }) => doSetRequestableJobId(requestablejobid || undefined, true));
  useSubscribe('selectCrew', ({ callsign }) => doSetCallsign(callsign, true));
  useSubscribe('selectPCR', ({ pcrid }) => doSetPcrID(pcrid, true));
  useSubscribe('selectScheduledJob', ({ scheduleid }) => doSetScheduledJobId(scheduleid, true));
  useSubscribe('selectListEntry', ({ entryid }) => doSetListEntryID(entryid, true));
  useSubscribe('selectList', ({ listid }) => doSetListID(listid, true));
  useSubscribe('selectLocation', ({ locationid }) => doSetLocationID(locationid, true));
  useSubscribe('createJob', (data) => {
    updateJob(network, data);
    setActiveJob(dispatch, undefined);
  })
  useSubscribe('childOpened', ({ child }) => {
    child.postMessage({ type: 'selectJob', params: { jobid: jobId }});
    child.postMessage({ type: 'selectRequestableJob', params: { requestablejobid: requestableJobId }});
    child.postMessage({ type: 'selectCrew', params: { callsign }});
    child.postMessage({ type: 'selectPCR', params: { pcrid: pcrID }});
    child.postMessage({ type: 'selectScheduledJob', params: { scheduleid: scheduledJobId }});
    child.postMessage({ type: 'selectListEntry', params: { entryid: listEntryID }});
    child.postMessage({ type: 'selectList', params: { listid: listID }});
    child.postMessage({ type: 'selectLocation', params: { locationid: locationID }});
  }, [jobId, callsign, pcrID, scheduledJobId, listEntryID, listID, locationID, requestableJobId]);  
  
  const doSetJobId = useCallback((jobid, excludeBroadcast) => {
    if (jobid !== jobId) {
        if (!excludeBroadcast) {
            broadcast('selectJob', { jobid: jobid });
        }
        resetNewJob(network);
        setActiveJob(dispatch, jobid);
        setRequestableJobId();
    }
  }, [jobId, network, dispatch, broadcast]);

 const doSetRequestableJobId = useCallback((requestablejobid, excludeBroadcast) => {
    if (requestablejobid !== requestableJobId){
        if(!excludeBroadcast){
            broadcast('selectRequestableJob', { requestablejobid: requestablejobid });
        }
        setActiveJob(dispatch, undefined);

        setRequestableJobId(requestablejobid);
    }
  }, [broadcast, dispatch, requestableJobId]);

  const doSetScheduledJobId = useCallback((scheduleid, excludeBroadcast) => {
    if (scheduleid !== scheduledJobId) {
      if (!excludeBroadcast) {
        broadcast('selectScheduledJob', { scheduleid: scheduleid });
      }
      setActiveScheduledJob(dispatch, scheduleid);
    }
  }, [broadcast, scheduledJobId, dispatch]);

  const doSetCallsign = useCallback((cs, excludeBroadcast) => {
    if (cs !== callsign) {
      if (!excludeBroadcast) {
        broadcast('selectCrew', { callsign: cs });
      }
      setCallsign(cs);
    }
  }, [broadcast, callsign]);

  const doSetPcrID = useCallback((id, excludeBroadcast) => {
    if (pcrID !== id) {
      if (!excludeBroadcast) {
        broadcast('selectPCR', { pcrid: id });
      }
      setPcrID(id)
    }
  }, [broadcast, pcrID])

  const doSetListID = useCallback((id, excludeBroadcast) => {
    if (listID !== id) {
      if (!excludeBroadcast) {
        broadcast('selectList', { listid: id });
      }
      setListID(id)
    }
  }, [broadcast, listID]);

  const doSetListEntryID = useCallback((id, excludeBroadcast) => {
    if (listEntryID !== id) {
      if (!excludeBroadcast) {
        broadcast('selectListEntry', { entryid: id });
      }
      setListEntryID(id)
    }
  }, [broadcast, listEntryID]);

  const doSetLocationID = useCallback((id, excludeBroadcast) => {
    if (locationID !== id) {
        if (!excludeBroadcast) {
            broadcast('selectLocation', { locationid: id });
        }
        setLocationID(id);
    }
  }, [broadcast, locationID]);

  const doPopout = useCallback((sid, args) => {
    popout('cad', { sid, args, cadState: { jobId, callsign, pcrID, listID, listEntryID, scheduledJobId, requestableJobId, locationID } });
  }, [popout, jobId, callsign, pcrID, listID, listEntryID, scheduledJobId, requestableJobId, locationID]);

  const value = useMemo(() => ({
    jobId,
    doSetJobId,
    callsign,
    doSetCallsign,
    pcrID,
    doSetPcrID,
    listID,
    setListID: doSetListID,
    listEntryID,
    setListEntryID: doSetListEntryID,
    scheduledJobId,
    requestableJobId,
    doSetRequestableJobId,
    doSetScheduledJobId,
    locationID,
    doSetLocationID,
    popout: doPopout
  }), [doSetCallsign, callsign, doSetJobId, jobId, pcrID, doSetPcrID, listID, doSetListID, listEntryID, doSetListEntryID, scheduledJobId, requestableJobId, doSetRequestableJobId, doSetScheduledJobId, locationID, doSetLocationID, doPopout]);

  return (
    <CADContext.Provider value={value}>
      {children}
    </CADContext.Provider>
  );
};
CADProvider.propTypes = {
  children: PropTypes.node
}

export const useSelectedJobid = () => {
  const context = useContext(CADContext);
  const info = useMemo(() => ({ selectedJobId: context?.jobId, setSelectedJobId: context?.doSetJobId ?? (() => {}) }), [context?.jobId, context?.doSetJobId])
  return info;
};

export const useSelectedRequestableJobid = () => {
  const context = useContext(CADContext);
  const info = useMemo(() => ({ selectedRequestableJobId: context?.requestableJobId, setSelectedRequestableJobId: context?.doSetRequestableJobId ?? (() => {}) }), [context?.requestableJobId, context?.doSetRequestableJobId]);
  return info;
}

export const useSelectedScheduledJobid = () => {
  const context = useContext(CADContext);
  const info = useMemo(() => ({ selectedScheduledJobId: context?.scheduledJobId, setSelectedScheduledJobId: context?.doSetScheduledJobId ?? (() => {}) }), [context?.scheduledJobId, context?.doSetScheduledJobId])
  return info;
};

export const useSelectedCallsign = () => {
  const context = useContext(CADContext);
  const info = useMemo(() => ({ selectedCallsign: context?.callsign, setSelectedCallsign: context?.doSetCallsign ?? (() => {})}), [context?.callsign, context?.doSetCallsign]);
  return info;
}

export const useSelectedPCR = () => {
  const context = useContext(CADContext);
  if (!context) {
    throw new Error('useSelectedPCR must be used within a CADProvider');
  }
  const info = useMemo(() => ({ selectedPcrID: context.pcrID, setSelectedPcrID: context.doSetPcrID }), [context.pcrID, context.doSetPcrID]);
  return info;
}

export const useSelectedListEntry = () => {
  const context = useContext(CADContext);
  if (!context) {
    throw new Error('useSelectedPCR must be used within a CADProvider');
  }
  const info = useMemo(() => ({ selectedListID: context.listID, setSelectedListID: context.setListID, selectedListEntryID: context.listEntryID, setSelectedListEntryID: (listid, entryid) => { context.setListID(listid); context.setListEntryID(entryid); } }), [context]);
  return info;
}

export const useSelectedLocation = () => {
    const context = useContext(CADContext);
    
    const info = useMemo(() => ({ selectedLocationID: context?.locationID, setSelectedLocationID: context?.doSetLocationID }), [context?.locationID, context?.doSetLocationID]);
    return info;
}

export const useCADPopout = () => {
    const context = useContext(CADContext);

    return context?.popout;
}